import {
  AmlPolicyReviewFrequency,
  LegalClientFinanceFinancialMonitoringInfoEntity,
  LegalClientFinancialMonitoringInfoOfficerEntity,
} from 'api/types/entity';
import { LegalClientFinancialMonitoringParam } from 'api/types/params';
import { Maybe } from 'types/maybe';
import { YesOrNoType } from 'types/yesOrNo';

import { CompanyFinanceFinancialMonitoringFormType } from '../../forms/companyFinanceFinancialMonitoringForm';

export const getFinanceFinancialMonitoringParam = (
  values: CompanyFinanceFinancialMonitoringFormType
): Omit<LegalClientFinancialMonitoringParam, 'globalInfo'> => {
  const isSubjectToRegulation =
    values.financeFinancialMonitoring.isSubjectToRegulation?.value ===
    YesOrNoType.Yes;

  if (!isSubjectToRegulation) {
    return { isSubjectToRegulation };
  }

  const hasAmlCftInternalControlOfficer =
    values.financeFinancialMonitoring.hasAmlCftInternalControlOfficer?.value ===
    YesOrNoType.Yes;

  const officerInfo:
    | Maybe<LegalClientFinancialMonitoringInfoOfficerEntity>
    | undefined = hasAmlCftInternalControlOfficer
    ? {
        email: values.financeFinancialMonitoring.officerEmail!,
        firstName: values.financeFinancialMonitoring.officerFirstName!,
        lastName: values.financeFinancialMonitoring.officerLastName!,
        phoneNumber: values.financeFinancialMonitoring.officerPhoneNumber!,
        position: values.financeFinancialMonitoring.officerPosition!,
        middleName: values.financeFinancialMonitoring.officerMiddleName,
      }
    : undefined;

  const frequencyReviewPolice =
    values.financeFinancialMonitoring.amlPolicyReviewFrequency?.value;

  const otherAmlPolicyReviewFrequencyField: Pick<
    LegalClientFinanceFinancialMonitoringInfoEntity,
    'otherAmlPolicyReviewFrequency'
  > =
    frequencyReviewPolice === AmlPolicyReviewFrequency.Other
      ? {
          otherAmlPolicyReviewFrequency:
            values.financeFinancialMonitoring.otherAmlPolicyReviewFrequency,
        }
      : {};

  const hasAmlCftDocumentEffectivenessAssessmentProcedures =
    values.financeFinancialMonitoring
      .hasAmlCftDocumentEffectivenessAssessmentProcedures?.value ===
    YesOrNoType.Yes;

  const documentProvisionRefusalReasonField: Pick<
    LegalClientFinanceFinancialMonitoringInfoEntity,
    'documentProvisionRefusalReason'
  > =
    values.financeFinancialMonitoring.canProvideAmlPolicyDocuments?.value ===
    YesOrNoType.No
      ? {
          documentProvisionRefusalReason:
            values.financeFinancialMonitoring.documentProvisionRefusalReason,
        }
      : {};

  const hadAmlCftInvestigationsOrPenalties =
    values.financeFinancialMonitoring.hadAmlCftInvestigationsOrPenalties
      ?.value === YesOrNoType.Yes;
  const investigationOrPenaltyReasonsField: Pick<
    LegalClientFinanceFinancialMonitoringInfoEntity,
    'investigationOrPenaltyReasons'
  > = hadAmlCftInvestigationsOrPenalties
    ? {
        investigationOrPenaltyReasons:
          values.financeFinancialMonitoring.investigationOrPenaltyReasons,
      }
    : {};

  const hasMandatoryControlTransactions =
    values.financeFinancialMonitoring.hasMandatoryControlTransactions?.value ===
    YesOrNoType.Yes;
  const mandatoryControlTransactionIdentificationProcedureField: Pick<
    LegalClientFinanceFinancialMonitoringInfoEntity,
    'mandatoryControlTransactionIdentificationProcedure'
  > = hasMandatoryControlTransactions
    ? {
        mandatoryControlTransactionIdentificationProcedure:
          values.financeFinancialMonitoring
            .mandatoryControlTransactionIdentificationProcedure,
      }
    : {};

  return {
    isSubjectToRegulation,
    financeInfo: {
      authorityName: values.financeFinancialMonitoring.authorityName!,
      authorityWebsite: values.financeFinancialMonitoring.authorityWebsite,
      hasAmlCftComplianceControl:
        values.financeFinancialMonitoring.hasAmlCftComplianceControl?.value ===
        YesOrNoType.Yes,
      hasTransactionMonitoringAndSuspiciousActivityDetection:
        values.financeFinancialMonitoring
          .hasTransactionMonitoringAndSuspiciousActivityDetection?.value ===
        YesOrNoType.Yes,
      amlPolicyReviewFrequency:
        values.financeFinancialMonitoring.amlPolicyReviewFrequency?.value!,
      ...otherAmlPolicyReviewFrequencyField,
      amlCftPoliciesApplyToForeignEntities:
        values.financeFinancialMonitoring.amlCftPoliciesApplyToForeignEntities
          ?.value === YesOrNoType.Yes,
      hasAmlCftDocumentEffectivenessAssessmentProcedures,
      dateOfLastCheck: values.financeFinancialMonitoring.dateOfLastCheck,
      hasAmlCftInternalControlOfficer,
      officer: officerInfo,
      canProvideAmlPolicyDocuments:
        values.financeFinancialMonitoring.canProvideAmlPolicyDocuments
          ?.value === YesOrNoType.Yes,
      ...documentProvisionRefusalReasonField,
      isInternalControlInfoConfidential:
        values.financeFinancialMonitoring.isInternalControlInfoConfidential
          ?.value === YesOrNoType.Yes,
      hadAmlCftInvestigationsOrPenalties,
      ...investigationOrPenaltyReasonsField,
      hasMandatoryControlTransactions,
      ...mandatoryControlTransactionIdentificationProcedureField,
      monetaryServiceTransactionCountForLastSixMonths:
        values.financeFinancialMonitoring
          .monetaryServiceTransactionCountForLastSixMonths?.value!,
      isAffiliatedWithNonProfitOrganizations:
        values.financeFinancialMonitoring.isAffiliatedWithNonProfitOrganizations
          ?.value === YesOrNoType.Yes,
      receivesFundingFromStateBudget:
        values.financeFinancialMonitoring.receivesFundingFromStateBudget
          ?.value === YesOrNoType.Yes,
      receivesFundingFromForeignNonProfitNGOs:
        values.financeFinancialMonitoring
          .receivesFundingFromForeignNonProfitNGOs?.value === YesOrNoType.Yes,
      isCompliantWithFATFRecommendations:
        values.financeFinancialMonitoring.isCompliantWithFATFRecommendations
          ?.value === YesOrNoType.Yes,
      hasBeenRefusedToOpenAccountByBanks:
        values.financeFinancialMonitoring.hasBeenRefusedToOpenAccountByBanks
          ?.value === YesOrNoType.Yes,
    },
  };
};
